import { useState, useEffect } from "react"
import { Plus, X } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from "@f2e/ui/components/base"

import {
  BrandOAuthProvider,
  ClientOAuthProvider,
  OAuthProviderEditData,
} from "@/types"

// Note: The schema needs to be conditional based on edit/create mode.
// We derive it below after knowing `isEditing`.
export type OAuthProviderFormData = {
  name: string
  provider: ClientOAuthProvider
  clientId: string
  clientSecret?: string
}

export interface OAuthProviderDialogProps {
  open: boolean
  initialData?: BrandOAuthProvider
  isLoading?: boolean
  onOpenChange?: (open: boolean) => void
  onSubmit?: (data: OAuthProviderEditData) => void
}

const OAuthProviderDialog = ({
  open,
  initialData,
  isLoading = false,
  onOpenChange,
  onSubmit,
}: OAuthProviderDialogProps) => {
  const { t } = useTranslation()

  const isEditing = !!initialData

  // Build schema based on mode:
  // - Create: clientSecret required
  // - Edit: clientSecret optional (empty means keep existing)
  const OAuthProviderFormSchema = z.object({
    name: z
      .string()
      .min(1, { message: t("brands.oauthForm.errors.nameRequired") }),
    provider: z.nativeEnum(ClientOAuthProvider),
    clientId: z
      .string()
      .min(1, { message: t("brands.oauthForm.errors.clientIdRequired") }),
    clientSecret: isEditing
      ? z.string().optional()
      : z.string().min(1, {
          message: t("brands.oauthForm.errors.clientSecretRequired"),
        }),
  })
  const [scopes, setScopes] = useState<string[]>(initialData?.scopes || [])
  const [scopeInput, setScopeInput] = useState("")

  const form = useForm<OAuthProviderFormData>({
    resolver: zodResolver(OAuthProviderFormSchema),
    values: {
      name: initialData?.name || "",
      provider: initialData?.provider || ClientOAuthProvider.Google,
      clientId: initialData?.clientId || "",
      // Never show secret preview in the input field
      clientSecret: "",
    },
  })

  useEffect(() => {
    if (!open) return
    setScopes(initialData?.scopes || [])
  }, [open, initialData?.scopes])

  const handleSubmit = (data: OAuthProviderFormData) => {
    const submitData: OAuthProviderEditData = {
      name: data.name,
      provider: data.provider,
      clientId: data.clientId,
      scopes: scopes,
      clientSecret: data.clientSecret?.trim() || "",
    }

    onSubmit?.(submitData)
    form.reset()
    setScopes([])
    setScopeInput("")
  }

  const handleCancel = () => {
    form.reset()
    setScopes(initialData?.scopes || [])
    setScopeInput("")
    onOpenChange?.(false)
  }

  const handleAddScope = () => {
    const trimmedScope = scopeInput.trim()
    if (trimmedScope && !scopes.includes(trimmedScope)) {
      setScopes([...scopes, trimmedScope])
      setScopeInput("")
    }
  }

  const handleRemoveScope = (scopeToRemove: string) => {
    setScopes(scopes.filter((scope) => scope !== scopeToRemove))
  }

  const handleScopeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddScope()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t("brands.oauthForm.editTitle")
              : t("brands.oauthForm.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t("brands.oauthForm.editDescription")
              : t("brands.oauthForm.createDescription")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {!isEditing && (
              <FormField
                control={form.control}
                name="provider"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("brands.oauthForm.providerLabel")}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              "brands.oauthForm.providerPlaceholder"
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={ClientOAuthProvider.Google}>
                          Google
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("brands.oauthForm.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("brands.oauthForm.namePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("brands.oauthForm.clientIdLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("brands.oauthForm.clientIdPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientSecret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("brands.oauthForm.clientSecretLabel")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="off"
                      placeholder={t(
                        "brands.oauthForm.clientSecretPlaceholder"
                      )}
                      {...field}
                    />
                  </FormControl>
                  {isEditing && (
                    <FormDescription>
                      {t("brands.oauthForm.clientSecretHelp")}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>{t("brands.oauthForm.scopesLabel")}</FormLabel>
              <div className="flex gap-2">
                <Input
                  value={scopeInput}
                  onChange={(e) => setScopeInput(e.target.value)}
                  onKeyDown={handleScopeKeyDown}
                  placeholder={t("brands.oauthForm.scopesPlaceholder")}
                />
                <Button
                  size="icon"
                  variant="secondary"
                  onClick={handleAddScope}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {scopes.map((scope) => (
                  <Badge key={scope} variant="secondary" className="mt-1">
                    {scope}
                    <button
                      type="button"
                      onClick={() => handleRemoveScope(scope)}
                      className="ml-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                {t("brands.oauthForm.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? t("common.loading") : t("brands.oauthForm.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default OAuthProviderDialog
